import type { UndoableState } from "../../vendor/src/features/remotion-editor/editor/state/types";
import { volumeFn } from "../../vendor/src/features/remotion-editor/editor/utils/volume-fn";
import {isNarrationTrack} from './narration-track';

export type NarrationWindow = {
  itemId: string;
  url: string;
  delay: number;
  duration: number;
  sourceStart: number;
  rate: number;
  gain: number[];
};
export type NarrationPlan =
  | { ok: true; windows: NarrationWindow[] }
  | { ok: false; reason: string };
/** Preview projection only. Never splits or changes source assets or editorial state. */
export function sceneNarration(
  state: UndoableState,
  start: number,
  end: number,
): NarrationPlan {
  const { fps } = state;
  if (
    !Number.isFinite(fps) ||
    fps <= 0 ||
    !Number.isSafeInteger(start) ||
    !Number.isSafeInteger(end) ||
    start < 0 ||
    end <= start
  )
    return { ok: false, reason: "씬의 재생 구간을 확인하지 못했습니다." };
  const windows: NarrationWindow[] = [];
  for (const track of state.tracks) {
    if (
      !isNarrationTrack(track) ||
      track.muted
    )
      continue;
    for (const id of track.items) {
      const item = state.items[id];
      if (!item)
        return { ok: false, reason: "나레이션 항목을 찾지 못했습니다." };
      if (item.from >= end || item.from + item.durationInFrames <= start)
        continue;
      if (item.type !== "audio")
        return {
          ok: false,
          reason: "묶인 음성은 정밀 편집 재생으로 확인해 주세요.",
        };
      if (
        !Number.isSafeInteger(item.from) ||
        item.from < 0 ||
        !Number.isSafeInteger(item.durationInFrames) ||
        item.durationInFrames <= 0
      )
        return { ok: false, reason: "음성의 시간 정보를 확인하지 못했습니다." };
      const asset = state.assets[item.assetId];
      if (!asset || asset.type !== "audio" || !asset.remoteUrl)
        return { ok: false, reason: "음성 원본 연결을 확인해 주세요." };
      if (
        ![
          item.audioStartFromInSeconds,
          item.playbackRate,
          item.decibelAdjustment,
          item.audioFadeInDurationInSeconds,
          item.audioFadeOutDurationInSeconds,
        ].every(Number.isFinite) ||
        item.audioStartFromInSeconds < 0 ||
        item.playbackRate <= 0 ||
        item.audioFadeInDurationInSeconds < 0 ||
        item.audioFadeOutDurationInSeconds < 0
      )
        return {
          ok: false,
          reason: "음성의 시작점이나 배속을 확인하지 못했습니다.",
        };
      const from = Math.max(start, item.from),
        to = Math.min(end, item.from + item.durationInFrames);
      const volume = volumeFn({
        fps,
        durationInFrames: item.durationInFrames,
        decibelAdjustment: item.decibelAdjustment,
        audioFadeInDurationInSeconds: item.audioFadeInDurationInSeconds,
        audioFadeOutDurationInSeconds: item.audioFadeOutDurationInSeconds,
      });
      const samples = item.audioFadeInDurationInSeconds || item.audioFadeOutDurationInSeconds
        ? Math.min(65537, Math.max(129, (to-from)*4+1)) : 2;
      const gain = Array.from({ length: samples }, (_, i) =>
        volume(from - item.from + ((to - from) * i) / (samples-1)),
      );
      if (!gain.every((value) => Number.isFinite(value) && value >= 0))
        return { ok: false, reason: "음성의 음량을 확인하지 못했습니다." };
      windows.push({
        itemId: id,
        url: asset.remoteUrl,
        delay: (from - start) / fps,
        duration: (to - from) / fps,
        sourceStart:
          item.audioStartFromInSeconds +
          ((from - item.from) / fps) * item.playbackRate,
        rate: item.playbackRate,
        gain,
      });
    }
  }
  return { ok: true, windows: windows.sort((a, b) => a.delay - b.delay) };
}
